const P = '/assets/products';

export const PRODUCT_CATEGORIES = [
  {
    id: 'air-conditioners',
    h2: 'Air Conditioners',
    intro: 'Advanced, energy-efficient central cooling engineered for quiet, dependable comfort through the hottest North Carolina summers.',
    products: [
      {
        name: 'Central Air Conditioners',
        tag: 'Platinum · Gold · Silver Series',
        desc: 'From the variable-speed Platinum series to the dependable Silver series, American Standard air conditioners deliver premium cooling with efficiency options for every budget.',
        features: ['Energy-efficient cooling up to top-tier SEER2 ratings', 'Multiple cooling speeds keep costs down', 'Extremely quiet performance', 'Premium, year-round comfort'],
        img: `${P}/air-conditioners.jpg`,
        alt: 'American Standard Platinum central air conditioner installed by B.E. Holbrooks Co. in Stanfield, NC',
      },
    ],
  },
  {
    id: 'heat-pumps',
    h2: 'Heat Pumps',
    intro: 'One system for heating, cooling, and humidity control — an ideal fit for our moderate Stanly County climate.',
    products: [
      {
        name: 'All-Electric Heat Pumps',
        tag: 'Heating + Cooling in One',
        desc: 'Versatile, all-electric comfort that heats in winter, cools in summer, and manages humidity all year — with efficiency ratings that keep energy costs low.',
        features: ['Heats and cools from a single system', 'Ideal for moderate North Carolina winters', 'High efficiency reduces energy bills', 'Lowers your home\'s carbon footprint'],
        img: `${P}/heat-pumps.jpg`,
        alt: 'American Standard Silver 15 heat pump for year-round heating and cooling in Stanfield, NC',
      },
    ],
  },
  {
    id: 'furnaces',
    h2: 'Furnaces',
    intro: 'Long-lasting warmth from efficient gas furnaces built to pair perfectly with your air conditioner or heat pump.',
    products: [
      {
        name: 'Gas Furnaces',
        tag: 'Platinum · Gold · Silver Series',
        desc: 'American Standard furnaces deliver steady, efficient heat with variable-speed options that run quietly and trim gas costs all winter.',
        features: ['High-efficiency options lower gas costs', 'Variable-speed blowers for even, quiet heat', 'Pairs with ACs and heat pumps (dual fuel)', 'Built for long service life'],
        img: `${P}/furnaces.jpg`,
        alt: 'American Standard Gold S9V2 gas furnace installed by B.E. Holbrooks Co. near Stanfield, NC',
      },
    ],
  },
  {
    id: 'air-handlers-coils',
    h2: 'Air Handlers & Coils',
    intro: 'The indoor half of your comfort system — quiet air movement and durable heat transfer that protect efficiency.',
    products: [
      {
        name: 'Air Handlers',
        tag: 'Pairs with Heat Pumps & AC',
        desc: 'Insulated, quiet air handlers that circulate conditioned air evenly and help control humidity throughout your home.',
        features: ['Quiet, efficient air circulation', 'Compact designs for tight spaces', 'Insulated to prevent energy loss', 'Humidity control options'],
        img: `${P}/air-handlers.jpg`,
        alt: 'American Standard Platinum TAM9 air handler installed by B.E. Holbrooks Co.',
      },
      {
        name: 'Indoor Coils',
        tag: 'All-Aluminum Durability',
        desc: 'Rust-resistant, all-aluminum coils that transfer heat efficiently season after season and wipe down easily for cleaner air.',
        features: ['All-aluminum, rust-resistant construction', 'Efficient, quiet heat transfer', 'Easy to clean for better air quality', 'Matched to your outdoor unit'],
        img: `${P}/coils.jpg`,
        alt: 'American Standard Platinum indoor evaporator coil for efficient home cooling',
      },
    ],
  },
  {
    id: 'packaged-systems',
    h2: 'Packaged Systems',
    intro: 'All-in-one heating and cooling in a single outdoor cabinet — a smart fit for homes without basements or attics, and for mobile and modular homes.',
    products: [
      {
        name: 'Gas & Electric Packaged',
        tag: 'AC + Furnace in One',
        desc: 'An air conditioner and gas furnace bundled into one compact, quiet cabinet that maximizes efficiency by switching fuel sources.',
        features: ['All components in one compact unit', 'Quiet starts and shutdowns', 'Switches fuel sources for efficiency', 'Frees up indoor space'],
        img: `${P}/packaged.jpg`,
        alt: 'American Standard gas and electric packaged HVAC system for Stanfield NC homes',
        cover: true,
      },
      {
        name: 'Heat Pump Packaged',
        tag: 'All-Electric, Single Cabinet',
        desc: 'Heating and cooling from one ultra-quiet packaged unit — designed for energy savings in regions with mild winters like ours.',
        features: ['Helps save on energy costs', 'Ultra-quiet operation', 'Great for mild-winter climates', 'Single-footprint installation'],
        img: `${P}/packaged.jpg`,
        alt: 'American Standard heat pump packaged system providing year-round comfort',
        cover: true,
      },
      {
        name: 'Hybrid Dual Fuel Packaged',
        tag: 'Electric + Gas Flexibility',
        desc: 'A heat pump and gas furnace in one unit that automatically picks the most economical fuel source as temperatures change.',
        features: ['Two fuel sources, automatic switching', 'Efficient cooling and dehumidification', 'Warm gas heat on the coldest nights', 'Ideal where winters vary'],
        img: `${P}/packaged.jpg`,
        alt: 'American Standard hybrid dual fuel packaged HVAC system',
        cover: true,
      },
    ],
  },
  {
    id: 'ductless',
    h2: 'Ductless Mini-Split Systems',
    intro: 'Heating and cooling for one room, several rooms, or the whole house — no ductwork required.',
    products: [
      {
        name: 'Ductless Mini-Splits',
        tag: 'Zoned Comfort, No Ducts',
        desc: 'Perfect for additions, garages, sunrooms, and older homes — heat or cool only the spaces you use and cut energy costs.',
        features: ['No ductwork needed', 'Fast, minimally invasive installation', 'Room-by-room temperature control', 'Superior energy efficiency'],
        img: `${P}/ductless.jpg`,
        alt: 'American Standard ductless mini-split system installed in a Stanfield NC home',
        cover: true,
      },
    ],
  },
  {
    id: 'indoor-air-quality',
    h2: 'Indoor Air Quality',
    intro: 'Breathe easier with whole-home filtration, fresh-air ventilation, and humidity control integrated into your HVAC system.',
    products: [
      {
        name: 'Air Purification',
        tag: 'Up to 99.98% Particle Removal',
        desc: 'Whole-home air cleaners that remove dust, allergens, and airborne particles — up to 20 times more effective than standard filters.',
        features: ['Removes up to 99.98% of airborne particles', 'Far more effective than standard filters', 'Helps reduce allergens and toxins', 'Works with your existing system'],
        img: `${P}/air-purifier.jpg`,
        alt: 'American Standard whole-home air purification system for cleaner indoor air',
      },
      {
        name: 'Fresh-Air Ventilation',
        tag: 'Energy Recovery Ventilators',
        desc: 'Bring fresh outdoor air in while exhausting stale, humid air — reducing moisture, odors, and indoor pollutants.',
        features: ['Delivers fresh air without energy waste', 'Reduces moisture, odors, and pollutants', 'Helps stop window condensation', 'Balances temperature and humidity'],
        img: `${P}/ventilator.png`,
        alt: 'American Standard energy recovery ventilator for fresh indoor air',
      },
      {
        name: 'Humidity Control',
        tag: 'Humidifiers & Dehumidifiers',
        desc: 'Whole-home humidity management that limits mold and bacteria, protects wood furnishings, and keeps every season comfortable.',
        features: ['Limits bacteria, mold, and viruses', 'Options for homes up to 5,000 sq ft', 'Comfort control in every season', 'Integrates with your HVAC system'],
        img: `${P}/humidifier.png`,
        alt: 'American Standard Platinum steam humidifier for whole-home humidity control',
      },
    ],
  },
  {
    id: 'controls',
    h2: 'Thermostats & Controls',
    intro: 'Easy-to-use controls that put your whole comfort system — including zoning — at your fingertips.',
    products: [
      {
        name: 'Smart Thermostats',
        tag: 'WiFi Connected',
        desc: 'Control your home\'s temperature from anywhere, save energy automatically, and get system diagnostics on your phone.',
        features: ['Control from phone, tablet, or computer', 'Energy-saving schedules and modes', 'Built-in system diagnostics', 'Works with Alexa and Google Home'],
        img: `${P}/smart-thermostat.jpg`,
        alt: 'American Standard AccuLink Platinum smart thermostat installed by B.E. Holbrooks Co.',
      },
      {
        name: 'Traditional & Zoning Controls',
        tag: 'Simple + Room-by-Room',
        desc: 'Dependable programmable thermostats and zoning systems that direct heating and cooling exactly where you need it.',
        features: ['Simple, reliable programming', 'Zoning for room-by-room control', 'Directs air only where needed', 'Connects to the Home App'],
        img: `${P}/smart-thermostat.jpg`,
        alt: 'American Standard programmable thermostat and zoning controls',
      },
    ],
  },
  {
    id: 'light-commercial',
    h2: 'Light Commercial',
    intro: 'Comfort systems for offices, retail spaces, churches, and buildings — from three to twenty-five tons.',
    products: [
      {
        name: 'Light Commercial Systems',
        tag: '3–25 Ton Capacity',
        desc: 'Split and packaged systems sized for larger spaces, delivering efficient, reliable heating and cooling for your business.',
        features: ['Sized for offices, retail & more', 'Keeps energy usage low', 'Split or packaged configurations', 'Flexible fit for many building types'],
        img: `${P}/commercial.jpg`,
        alt: 'American Standard Odyssey light commercial HVAC system for businesses in the Charlotte NC area',
        cover: true,
      },
    ],
  },
];
